import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getUser, isDemo } from '../services/auth';
import {
  Close,
  GetApp,
  PhoneAndroid,
  PhoneIphone,
  AddToHomeScreen,
  ExpandMore,
  Computer
} from '@mui/icons-material';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallPromptProps {
  forceShowInstructions?: boolean;
  onCloseInstructions?: () => void;
}

// Variable global para controlar el componente desde fuera
let openInstructionsCallback: (() => void) | null = null;

export const openPWAInstallInstructions = () => {
  if (openInstructionsCallback) {
    openInstructionsCallback();
  }
};

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ 
  forceShowInstructions = false,
  onCloseInstructions 
}) => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [, setIsInstalled] = useState(false);
  const [, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [, setIsStandalone] = useState(false);
  const [, setBrowser] = useState<'chrome' | 'firefox' | 'safari' | 'samsung' | 'edge' | 'other'>('other');

  // Detectar tipo de dispositivo, navegador y si ya está instalado
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Detectar iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Detectar Android
    const android = /Android/.test(navigator.userAgent);
    setIsAndroid(android);

    // Detectar navegador específico
    let detectedBrowser: 'chrome' | 'firefox' | 'safari' | 'samsung' | 'edge' | 'other' = 'other';
    
    if (userAgent.includes('samsungbrowser')) {
      detectedBrowser = 'samsung';
    } else if (userAgent.includes('edg/') || userAgent.includes('edga') || userAgent.includes('edgios')) {
      detectedBrowser = 'edge';
    } else if (userAgent.includes('firefox') || userAgent.includes('fxios')) {
      detectedBrowser = 'firefox';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('chromium')) {
      detectedBrowser = 'safari';
    } else if (userAgent.includes('chrome') || userAgent.includes('chromium') || userAgent.includes('crios')) {
      detectedBrowser = 'chrome';
    }
    
    setBrowser(detectedBrowser);

    // Detectar si ya está instalado (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    // Verificar si ya está instalado revisando localStorage
    const wasInstalled = localStorage.getItem('pwa-installed') === 'true';
    setIsInstalled(wasInstalled || standalone);

    // Para Android: escuchar el evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Manejar instalación en Android
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Si no hay prompt nativo, mostrar instrucciones
      setShowInstructions(true);
      return;
    }

    try {
      // Mostrar el prompt de instalación nativo
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem('pwa-installed', 'true');
        setShowBanner(false);
        setDeferredPrompt(null);
      }
    } catch (error) {
      console.error('Error al instalar PWA:', error);
      // Si falla, mostrar instrucciones manuales
      setShowInstructions(true);
    }
  };

  // Cerrar banner
  const handleCloseBanner = () => {
    setShowBanner(false);
    // Guardar que el usuario cerró el banner (no mostrar por 7 días)
    // EXCEPCIÓN: Usuarios Demo no guardan el flag, siempre se mostrará de nuevo
    const user = getUser();
    if (user && !isDemo(user)) {
      localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
    }
  };

  // Verificar si el banner fue cerrado recientemente y mostrar después de 1 segundo
  useEffect(() => {
    // Pequeño delay para asegurar que los estados se hayan establecido
    const checkAndShowBanner = setTimeout(() => {
      // Verificar nuevamente si está instalado (puede haber cambiado)
      const standaloneCheck = window.matchMedia('(display-mode: standalone)').matches ||
                             (window.navigator as any).standalone === true;
      const wasInstalledCheck = localStorage.getItem('pwa-installed') === 'true';
      
      if (wasInstalledCheck || standaloneCheck) {
        return;
      }

      // Obtener usuario actual
      const user = getUser();
      const userIsDemo = user ? isDemo(user) : false;
      
      let shouldShow = true;
      
      // EXCEPCIÓN: Usuarios Demo siempre ven el banner, sin importar el timer
      if (!userIsDemo) {
        const dismissedTime = localStorage.getItem('pwa-banner-dismissed');
        
        if (dismissedTime) {
          const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
          if (daysSinceDismissed < 7) {
            // Si fue cerrado hace menos de 7 días, no mostrar automáticamente
            shouldShow = false;
          } else {
            // Si pasaron más de 7 días, limpiar el flag
            localStorage.removeItem('pwa-banner-dismissed');
          }
        }
      }
      // Si es Demo, shouldShow permanece en true (siempre mostrar)

      // Mostrar banner después de 1 segundo si debe mostrarse
      if (shouldShow) {
        setShowBanner(true);
      }
    }, 1000); // 1 segundo después de que el componente se monte

    return () => {
      clearTimeout(checkAndShowBanner);
    };
  }, []); // Sin dependencias para que se ejecute solo una vez al montar

  // Exponer función para abrir instrucciones desde fuera
  useEffect(() => {
    openInstructionsCallback = () => {
      // Re-detectamos el dispositivo/navegador al abrir manualmente de forma síncrona
      const userAgent = navigator.userAgent.toLowerCase();
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      const android = /Android/.test(navigator.userAgent);
      
      // Detectar navegador
      let detectedBrowser: 'chrome' | 'firefox' | 'safari' | 'samsung' | 'edge' | 'other' = 'other';
      if (userAgent.includes('samsungbrowser')) {
        detectedBrowser = 'samsung';
      } else if (userAgent.includes('edg/') || userAgent.includes('edga') || userAgent.includes('edgios')) {
        detectedBrowser = 'edge';
      } else if (userAgent.includes('firefox') || userAgent.includes('fxios')) {
        detectedBrowser = 'firefox';
      } else if (userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('chromium')) {
        detectedBrowser = 'safari';
      } else if (userAgent.includes('chrome') || userAgent.includes('chromium') || userAgent.includes('crios')) {
        detectedBrowser = 'chrome';
      }
      
      // Actualizar estados de forma síncrona
      setIsIOS(iOS);
      setIsAndroid(android);
      setBrowser(detectedBrowser);
      
      // Mostrar instrucciones inmediatamente
      setShowInstructions(true);
    };
    return () => {
      openInstructionsCallback = null;
    };
  }, []);

  // Si se fuerza mostrar instrucciones
  useEffect(() => {
    if (forceShowInstructions) {
      setShowInstructions(true);
    }
  }, [forceShowInstructions]);

  // Banner flotante en la parte inferior
  // No verificar isInstalled/isStandalone aquí porque pueden no estar inicializados aún
  // El useEffect ya maneja la lógica de mostrar/ocultar
  if (showBanner && !showInstructions) {
    return (
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: '90%',
          width: '400px',
          backgroundColor: '#1a1a1a',
          border: '2px solid #2391FF',
          borderRadius: '16px',
          padding: 2,
          zIndex: 10000,
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GetApp sx={{ color: '#2391FF', fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '18px' }}>
              {t('pwa.install.bannerTitle')}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleCloseBanner}
            sx={{ color: '#888' }}
          >
            <Close />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ color: '#d9d9d9', mb: 1, fontSize: '14px' }}>
          {t('pwa.install.bannerDescription')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', mb: 2, fontSize: '12px', fontStyle: 'italic' }}>
          {t('pwa.install.bannerNote')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleInstallClick}
            fullWidth
            sx={{
              backgroundColor: '#2391FF',
              color: '#000',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#1a7ad9'
              }
            }}
            startIcon={<AddToHomeScreen />}
          >
            {isAndroid && deferredPrompt ? t('pwa.install.installNow') : t('pwa.install.viewInstructions')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleCloseBanner}
            sx={{
              borderColor: '#666',
              color: '#d9d9d9',
              textTransform: 'none',
              borderRadius: '8px',
              minWidth: '80px'
            }}
          >
            {t('pwa.install.notNow')}
          </Button>
        </Box>
      </Paper>
    );
  }

  // Dialog con instrucciones paso a paso
  if (showInstructions) {
    return (
      <Dialog
        open={showInstructions}
        onClose={() => {
          setShowInstructions(false);
          if (onCloseInstructions) {
            onCloseInstructions();
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a1a',
            border: '2px solid #2391FF',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#2391FF', fontWeight: 'bold', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GetApp sx={{ fontSize: 28 }} />
              <Typography variant="h6">{t('pwa.install.title')}</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setShowInstructions(false)}
              sx={{ color: '#888' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box>
            <Typography variant="body2" sx={{ color: '#888', mb: 2, textAlign: 'center', fontSize: '12px' }}>
              {t('pwa.install.selectDevice')}
            </Typography>
            
            {/* iPhone (iOS) - Safari */}
            <Accordion sx={{ backgroundColor: '#2a2a2a', mb: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#2391FF' }} />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
              >
                <PhoneIphone sx={{ color: '#2391FF', mr: 1, fontSize: 24 }} />
                <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {t('pwa.install.ios.title')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: '#ff9800',
                      color: '#000',
                      borderRadius: '8px',
                      p: 1,
                      mb: 2,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                      {t('pwa.install.ios.safariOnly')}
                    </Typography>
                  </Box>
                  
                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.ios.step1Title')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 1.5, fontSize: '12px' }}>
                    {t('pwa.install.ios.step1Description')}
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.ios.step2Title')}
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5, mt: 1.5 }}>
                    {t('pwa.install.ios.step3Title')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 0.5, fontSize: '12px' }}>
                    {t('pwa.install.ios.step3Description')}
                  </Typography>
                  <Typography sx={{ color: '#888', mb: 1.5, fontSize: '11px' }}>
                    {t('pwa.install.ios.step3Note')}
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.ios.step4Title')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 1.5, fontSize: '12px' }}>
                    <strong style={{ color: '#2391FF' }}>{t('pwa.install.ios.step4Description')}</strong>
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.ios.step5Title')}
                  </Typography>
                  <Typography sx={{ color: '#4caf50', fontSize: '12px', mt: 0.5, fontWeight: 'bold' }}>
                    {t('pwa.install.ios.step5Confirm')}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Android - Chrome */}
            <Accordion sx={{ backgroundColor: '#2a2a2a', mb: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#2391FF' }} />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
              >
                <PhoneAndroid sx={{ color: '#2391FF', mr: 1, fontSize: 24 }} />
                <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {t('pwa.install.android.title')} - Chrome
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 1 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.android.chrome.step1Title')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 1.5, fontSize: '12px' }}>
                    {t('pwa.install.android.chrome.step1Description')}
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 1.5 }}>
                    {t('pwa.install.android.chrome.step2Title')}
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.android.chrome.step3Title')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 1.5, fontSize: '12px' }}>
                    {t('pwa.install.android.chrome.step3Description')}
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 1.5 }}>
                    {t('pwa.install.android.chrome.step4Title')}
                  </Typography>

                  <Typography sx={{ color: '#4caf50', fontSize: '12px', fontWeight: 'bold' }}>
                    {t('pwa.install.android.chrome.step4Confirm')}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Android - Firefox */}
            <Accordion sx={{ backgroundColor: '#2a2a2a', mb: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#2391FF' }} />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
              >
                <PhoneAndroid sx={{ color: '#2391FF', mr: 1, fontSize: 24 }} />
                <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {t('pwa.install.android.title')} - Firefox
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 1 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.android.firefox.step1Title')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 1.5, fontSize: '12px' }}>
                    {t('pwa.install.android.firefox.step1Description')}
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.android.firefox.step2Title')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 1.5, fontSize: '12px' }}>
                    {t('pwa.install.android.firefox.step2Description')} <strong style={{ color: '#2391FF' }}>{t('pwa.install.android.firefox.step2Option1')}</strong> o{' '}
                    <strong style={{ color: '#2391FF' }}>{t('pwa.install.android.firefox.step2Option2')}</strong>
                  </Typography>

                  <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', mb: 0.5 }}>
                    {t('pwa.install.android.firefox.step3Title')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 1.5, fontSize: '12px' }}>
                    {t('pwa.install.android.firefox.step3Description')}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Desktop */}
            <Accordion sx={{ backgroundColor: '#2a2a2a', '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#2391FF' }} />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
              >
                <Computer sx={{ color: '#2391FF', mr: 1, fontSize: 24 }} />
                <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {t('pwa.install.desktop.title')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 1 }}>
                  <Typography sx={{ color: '#d9d9d9', mb: 1, fontSize: '12px' }}>
                    {t('pwa.install.desktop.step1')}
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', mb: 1, fontSize: '12px' }}>
                    {t('pwa.install.desktop.step2')} <strong style={{ color: '#2391FF' }}>{t('pwa.install.desktop.step2Option1')}</strong> o{' '}
                    <strong style={{ color: '#2391FF' }}>{t('pwa.install.desktop.step2Option2')}</strong>
                  </Typography>
                  <Typography sx={{ color: '#d9d9d9', fontSize: '12px' }}>
                    {t('pwa.install.desktop.step3')}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setShowInstructions(false);
              setShowBanner(false);
              if (onCloseInstructions) {
                onCloseInstructions();
              }
            }}
            fullWidth
            sx={{
              backgroundColor: '#2391FF',
              color: '#000',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#1a7ad9'
              }
            }}
          >
            {t('pwa.install.understood')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
};

export default PWAInstallPrompt;

